import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface ExchangeRates {
  [currency: string]: number;
}

interface CurrencyConversionResult {
  preferredCurrency: string;
  convertAmount: (amount: number, fromCurrency: string) => number;
  isLoading: boolean;
}

export function useCurrencyConversion(): CurrencyConversionResult {
  const { user } = useAuth();
  const [preferredCurrency, setPreferredCurrency] = useState<string>('USD');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPreferredCurrencyAndRates = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        // Get user's preferred currency from profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('default_currency')
          .eq('user_id', user.id)
          .single();

        if (profileError) throw profileError;

        const currency = profile?.default_currency || 'USD';
        setPreferredCurrency(currency);

        // Fetch exchange rates for the preferred currency
        const { data: ratesData, error: ratesError } = await supabase.functions.invoke(
          'get-exchange-rates',
          {
            body: { baseCurrency: currency }
          }
        );

        if (ratesError) throw ratesError;

        setExchangeRates(ratesData.rates || {});
      } catch (error) {
        console.error('Error loading currency data:', error);
        // Fallback to USD with 1:1 rates
        setPreferredCurrency('USD');
        setExchangeRates({ USD: 1 });
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferredCurrencyAndRates();
  }, [user]);

  const convertAmount = (amount: number, fromCurrency: string): number => {
    if (!fromCurrency || fromCurrency === preferredCurrency) {
      return amount;
    }

    // Convert from source currency to preferred currency
    // First convert to base (preferred currency), then to target
    const rate = exchangeRates[fromCurrency];
    
    if (!rate) {
      console.warn(`Exchange rate not found for ${fromCurrency}, returning original amount`);
      return amount;
    }

    // Since our base is the preferred currency, we need to divide
    // Example: if base is USD and rate for EUR is 0.85, then 100 EUR = 100 / 0.85 USD
    return amount / rate;
  };

  return {
    preferredCurrency,
    convertAmount,
    isLoading
  };
}
